import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { Tooltip } from "primereact/tooltip";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLazyGetBenefitLineAnalysisQuery } from "store/queries/benefitComparison";
import {
  ComparisonBenefit,
  IComparisonExample,
} from "types/BenefitComparison/BenefitComparison";
import {
  ILineAnalysis,
  ILineAnalysisRow,
  LineAnalysisLocation,
  LineAnalysisStatus,
} from "types/BenefitComparison/LineAnalysis";

import { formatComparisonValue } from "./benefitComparisonWording";

/**
 * "Analyze line items" — on the Engine worklist's response panel.
 *
 * The button re-runs today's engine on the selected check with recording on; every row of the panel
 * then carries a marker (Picked / Dropped / Not used / Not read) whose tooltip is the engine's own
 * reason, filled into a fixed sentence on the server. 🚨 No AI is involved anywhere in this.
 *
 * Kept in its own file, with the panel touching it at three points only (the provider around the
 * panel body, the button, and one marker per row), so the panel file stays easy to merge.
 */

export const ANALYZE_BUTTON_LABEL = "Analyze line items";

export const LINE_ANALYSIS_STATUS_LABELS: Record<LineAnalysisStatus, string> = {
  PICKED: "Picked",
  DROPPED: "Dropped",
  NOT_USED: "Not used",
  NOT_READ: "Not read",
};

const STATUS_TAG: Record<
  LineAnalysisStatus,
  { severity?: "success" | "danger" | "warning"; className?: string }
> = {
  PICKED: { severity: "success" },
  DROPPED: { severity: "danger" },
  NOT_USED: { severity: "warning" },
  NOT_READ: { className: "bg-gray-200 text-900" },
};

/** Matched by one `<Tooltip>` per analysis — PrimeReact needs a mounted Tooltip targeting the class. */
const MARKER_CLASS = "lineAnalysisMarker";

const keyOf = (location: LineAnalysisLocation) =>
  location.type === "LINE"
    ? `L:${location.serviceIndex}:${location.lineIndex}`
    : `S:${location.block}:${location.field}`;

interface ILineAnalysisContext {
  analysis: ILineAnalysis | null;
  rows: Map<string, ILineAnalysisRow>;
  shown: Set<string>;
  benefit: ComparisonBenefit;
  run: () => void;
  isFetching: boolean;
  isError: boolean;
}

const LineAnalysisContext = createContext<ILineAnalysisContext | null>(null);

/**
 * Holds one check's analysis for the panel inside it. Selecting another check drops it: an analysis
 * is of one check and must never be shown against another's lines.
 */
export function LineAnalysisProvider({
  example,
  benefit,
  children,
}: {
  example: IComparisonExample;
  benefit: ComparisonBenefit;
  children: ReactNode;
}) {
  const [trigger, { data, isFetching, isError }] =
    useLazyGetBenefitLineAnalysisQuery();
  const [requested, setRequested] = useState(false);
  useEffect(() => setRequested(false), [example.pverifyResponseId, benefit]);

  const current = data?.data;
  const analysis =
    requested &&
    current &&
    current.pverifyResponseId === example.pverifyResponseId &&
    current.benefit === benefit
      ? current
      : null;

  const value = useMemo<ILineAnalysisContext>(() => {
    const rows = new Map<string, ILineAnalysisRow>(
      (analysis?.lines ?? []).map((row) => [keyOf(row.location), row])
    );
    const shown = new Set<string>([
      ...example.summaries.map((summary) =>
        keyOf({ type: "SUMMARY", block: summary.block, field: summary.field })
      ),
      ...example.lines.map((line) =>
        keyOf({
          type: "LINE",
          serviceIndex: line.serviceIndex,
          lineIndex: line.lineIndex,
        })
      ),
    ]);
    return {
      analysis,
      rows,
      shown,
      benefit,
      run: () => {
        setRequested(true);
        trigger({ pverifyResponseId: example.pverifyResponseId, benefit });
      },
      isFetching,
      isError: requested && isError,
    };
  }, [analysis, example, benefit, trigger, isFetching, isError, requested]);

  return (
    <LineAnalysisContext.Provider value={value}>
      {analysis && (
        <Tooltip
          key={`${analysis.pverifyResponseId}:${analysis.benefit}`}
          target={`.${MARKER_CLASS}`}
          position="left"
        />
      )}
      {children}
    </LineAnalysisContext.Provider>
  );
}

const useLineAnalysis = () => useContext(LineAnalysisContext);

function StatusTag({ row }: { row: ILineAnalysisRow }) {
  const label = LINE_ANALYSIS_STATUS_LABELS[row.status];
  const tag = STATUS_TAG[row.status];
  return (
    <Tag
      value={label}
      severity={tag.severity}
      className={`${MARKER_CLASS} ${tag.className ?? ""}`}
      data-pr-tooltip={row.reason}
      aria-label={`${label}: ${row.reason}`}
      data-testid={`lineAnalysis.marker.${row.path}`}
    />
  );
}

/** One row's marker, beside its tags. Nothing until the analysis has run for this check. */
export function LineAnalysisMarker({
  location,
}: {
  location: LineAnalysisLocation;
}) {
  const context = useLineAnalysis();
  const row = context?.rows.get(keyOf(location));
  return row ? <StatusTag row={row} /> : null;
}

function RowList({ rows }: { rows: ILineAnalysisRow[] }) {
  const context = useLineAnalysis();
  return (
    <ul className="list-none p-0 m-0 flex flex-column gap-2 text-sm">
      {rows.map((row) => (
        <li key={row.path} className="flex gap-2 align-items-start">
          <StatusTag row={row} />
          <span>
            <span className="font-mono">{row.path}</span>
            {row.serviceName && ` · ${row.serviceName}`}
            {row.message.length > 0 && ` · ${row.message.join(" | ")}`}
            {row.value !== null &&
              context &&
              ` · ${formatComparisonValue(row.value, context.benefit)}`}
            <span className="block text-color-secondary">{row.reason}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

function Group({
  label,
  rows,
  testId,
}: {
  label: string;
  rows: ILineAnalysisRow[];
  testId: string;
}) {
  const [open, setOpen] = useState(false);
  if (rows.length === 0) return null;
  return (
    <div data-testid={testId}>
      <Button
        type="button"
        icon={open ? "pi pi-angle-down" : "pi pi-angle-right"}
        label={label}
        className="p-button-text p-button-sm px-0"
        onClick={() => setOpen(!open)}
      />
      {open && <RowList rows={rows} />}
    </div>
  );
}

/**
 * The button, the one-line outcome, and the lines the panel does not show — those in the care
 * setting(s) the engine read in full, and the noise (other care settings, other kinds of line)
 * collapsed to a count.
 */
export function AnalyzeLineItems() {
  const context = useLineAnalysis();
  if (!context) return null;
  const { analysis, shown, run, isFetching, isError } = context;

  const hidden = (analysis?.lines ?? []).filter(
    (row) => !shown.has(keyOf(row.location))
  );
  const readHere = hidden.filter(
    (row) => row.ofBenefit && row.careSetting === "READ"
  );
  const otherSettings = hidden.filter(
    (row) => row.ofBenefit && row.careSetting === "OTHER"
  );
  const otherKinds = hidden.filter((row) => !row.ofBenefit);

  return (
    <div className="mb-3" data-testid="lineAnalysis">
      <Button
        type="button"
        icon="pi pi-list"
        label={ANALYZE_BUTTON_LABEL}
        className="p-button-outlined p-button-sm"
        loading={isFetching}
        onClick={run}
        data-testid="lineAnalysis.run"
      />
      {isError && (
        <Message
          severity="error"
          className="ml-2"
          text="The line items could not be analysed."
        />
      )}
      {analysis && (
        <div className="mt-2 flex flex-column gap-2">
          <p className="m-0" data-testid="lineAnalysis.summary">
            {analysis.summary}
          </p>
          {analysis.note && (
            <Message
              severity="warn"
              text={analysis.note}
              data-testid="lineAnalysis.settingsChanged"
            />
          )}
          <Group
            label={`${readHere.length} more ${
              readHere.length === 1 ? "line" : "lines"
            } in the care ${
              analysis.readSources.length > 1 ? "settings" : "setting"
            } it read, not shown below`}
            rows={readHere}
            testId="lineAnalysis.readHere"
          />
          <Group
            label={`${otherSettings.length} ${
              otherSettings.length === 1 ? "line" : "lines"
            } in other care settings — not read`}
            rows={otherSettings}
            testId="lineAnalysis.otherSettings"
          />
          <Group
            label={`${otherKinds.length} ${
              otherKinds.length === 1 ? "line" : "lines"
            } of other kinds — not this benefit`}
            rows={otherKinds}
            testId="lineAnalysis.otherKinds"
          />
        </div>
      )}
    </div>
  );
}
